import React from "react";
import { translate } from "../../types/EnumTranslations";
import InfoPanel, { InfoItem } from "../../Components/InfoPanel";
import { BaseComponentImpl, RoomImpl, LevelImpl } from "../../types/implementations";
import { Building, SurfaceType, WindowType, DoorType } from "../../types/protocols";
import { BaseType, HazardType } from "../../types/Enums";
import BlueprintWithMarkers from "../../Components/BlueprintWithMarkers";
import PhotoGallery from "../../Components/PhotoGallery";
import ComponentCard from "../../Components/ComponentCard";
import PhotoWithMarker from "../../Components/PhotoWithMarker";
import { countWindowsForComponent, countDoorsForComponent } from "../../Helpers/utils";
import { useNavigate } from "react-router-dom";

// Helper function to get custom type for a component
function getCustomTypeForComponent(
    component: BaseComponentImpl,
    building: Building
): SurfaceType | WindowType | DoorType | null {
    if (!component.typeKey?.value || !building.model.structure?.types) {
        return null;
    }

    const types = building.model.structure.types;
    const typeKey = component.typeKey.value;

    if (component.type === BaseType.Wall || component.type === BaseType.Ceiling || component.type === BaseType.Floor) {
        return types.surfaceTypes?.[typeKey] || null;
    } else if (component.type === BaseType.Window) {
        return types.windowTypes?.[typeKey] || null;
    } else if (component.type === BaseType.Door) {
        return types.doorTypes?.[typeKey] || null;
    }

    return null;
}

// Helper function to get risks from custom type
function getCustomTypeRisks(customType: SurfaceType | WindowType | DoorType | null): string[] {
    if (!customType?.buildingRisks?.value) {
        return [];
    }

    const riskConstruction = customType.buildingRisks.value;
    const risks: string[] = [];

    if (riskConstruction.risks && riskConstruction.risks.length > 0) {
        riskConstruction.risks.forEach(risk => {
            risks.push(translate(risk));
        });
    }

    if (riskConstruction.customRisk) {
        risks.push(riskConstruction.customRisk);
    }

    return risks;
}

// Helper function to get hazards from custom type
function getCustomTypeHazards(customType: SurfaceType | WindowType | DoorType | null): string[] {
    if (!customType?.hazardous?.value) {
        return [];
    }

    const hazardModel = customType.hazardous.value;
    const hazards: string[] = [];

    if (hazardModel.led) {
        hazards.push(`${translate(HazardType.HazardLed)} (${translate(hazardModel.led)})`);
    }
    if (hazardModel.asbestos) {
        hazards.push(`${translate(HazardType.HazardAsbestos)} (${translate(hazardModel.asbestos)})`);
    }
    if (hazardModel.arsenic) {
        hazards.push(`${translate(HazardType.HazardArsenic)} (${translate(hazardModel.arsenic)})`);
    }
    if (hazardModel.blueConcrete) {
        hazards.push(`${translate(HazardType.HazardBlueConcrete)} (${translate(hazardModel.blueConcrete)})`);
    }
    if (hazardModel.radon) {
        hazards.push(`${translate(HazardType.HazardRadon)} (${translate(hazardModel.radon)})`);
    }

    return hazards;
}

interface ComponentDetailsViewProps {
    component: BaseComponentImpl;
    building: Building;
    buildingKey: string;
    showHeader?: boolean;
    headerActions?: React.ReactNode;
    containerStyle?: React.CSSProperties;
    onFancyboxOpen?: () => void;
    onFancyboxClose?: () => void;
    onImageClick?: (photo: any) => void;
}

const ComponentDetailsView: React.FC<ComponentDetailsViewProps> = ({
    component,
    building,
    buildingKey,
    showHeader = true,
    headerActions,
    containerStyle = {},
    onFancyboxOpen,
    onFancyboxClose,
    onImageClick
}) => {
    const navigate = useNavigate();
    const items = new Array<InfoItem>();
    
    // Get all components that have this component as parent, grouped by BaseType
    const childComponentsByType: Map<BaseType, BaseComponentImpl[]> = new Map();
    
    if (building.model?.structure?.components) {
        Object.values(building.model.structure.components).forEach(comp => {
            // Check all possible parent surface properties
            const parentSurfaces = [
                (comp as any).parentSurface?.value,
                (comp as any).parentSurface1?.value,
                (comp as any).parentSurface2?.value
            ].filter(Boolean);
            
            if (parentSurfaces.includes(component.key)) {
                const childComponent = new BaseComponentImpl(comp);
                const baseType = childComponent.type;
                
                if (!childComponentsByType.has(baseType)) {
                    childComponentsByType.set(baseType, []);
                }
                childComponentsByType.get(baseType)!.push(childComponent);
            }
        });
    }
    
    // Convert to sorted array by BaseType
    const sortedChildComponents = Array.from(childComponentsByType.entries())
        .sort(([typeA], [typeB]) => translate(typeA).localeCompare(translate(typeB), 'sv'));
    
    // For backward compatibility, keep windows reference
    const windows = childComponentsByType.get(BaseType.Window) || [];
    
    if (component.specificType) {
        items.push({
            label: "Typ",
            value: translate((component.specificType))
        })
    }
    if (component.label) {
        items.push({
            label: "Eget namn",
            value: component.label
        })
    }

    // Add level and room information if available
    // Determine the room key to use (roomKey, first parentRoomKey, or from parent surface)
    let effectiveRoomKey = component.roomKey;
    
    if (!effectiveRoomKey && component.parentRoomKeys?.value && component.parentRoomKeys.value.length > 0) {
        effectiveRoomKey = component.parentRoomKeys.value[0];
    }
    
    // Check parent surfaces if still no room key found
    if (!effectiveRoomKey && building.model.structure?.components) {
        const parentSurfaces = [
            component.parentSurface?.value,
            component.parentSurface1?.value,
            component.parentSurface2?.value
        ].filter(Boolean);
        
        for (const parentSurfaceKey of parentSurfaces) {
            if (!parentSurfaceKey) continue;
            
            const parentComponent = building.model.structure.components[parentSurfaceKey];
            if (parentComponent?.roomKey) {
                effectiveRoomKey = parentComponent.roomKey;
                break;
            }
        }
    }
    
    if (effectiveRoomKey && building.model.structure?.rooms) {
        const roomData = building.model.structure.rooms[effectiveRoomKey];
        
        if (roomData) {
            const room = new RoomImpl(roomData);
            
            // Add level information if room has a levelKey
            if (room.levelKey && building.model.structure?.levels) {
                const levelData = building.model.structure.levels[room.levelKey];
                
                if (levelData) {
                    const level = new LevelImpl(levelData);
                    items.push({
                        label: "Våning",
                        value: level.displayName()
                    });
                }
            }
            
            // Add room information
            items.push({
                label: "Rum",
                value: room.displayName()
            });
        }
    }

    if (component.cardinalDirection?.value) {
        items.push({
            label: "Väderstreck",
            value: translate(component.cardinalDirection.value)
        })
    }

    if (component.type === BaseType.Roof) {
        items.push({
                label: "Takform",
                value: translate(component.roofShape?.value ?? "")
            },
            {
                label: "Taklutning",
                value: String(component.roofSlope?.value ?? "-")
            }
        )
    }

    // Collect all hazards (component-level + custom type)
    const allHazards = new Array<string>();
    
    // Component-level hazards
    if (component.hazardous?.value) {
        const hazardModel = component.hazardous.value;
        if (hazardModel.led) {
            allHazards.push(`${translate(HazardType.HazardLed)} (${translate(hazardModel.led)})`);
        }
        if (hazardModel.asbestos) {
            allHazards.push(`${translate(HazardType.HazardAsbestos)} (${translate(hazardModel.asbestos)})`);
        }
        if (hazardModel.arsenic) {
            allHazards.push(`${translate(HazardType.HazardArsenic)} (${translate(hazardModel.arsenic)})`);
        }
        if (hazardModel.blueConcrete) {
            allHazards.push(`${translate(HazardType.HazardBlueConcrete)} (${translate(hazardModel.blueConcrete)})`);
        }
        if (hazardModel.radon) {
            allHazards.push(`${translate(HazardType.HazardRadon)} (${translate(hazardModel.radon)})`);
        }
    }

    // Custom type hazards and material
    const customType = getCustomTypeForComponent(component, building);
    const customTypeItems = new Array<InfoItem>();
    
    if (customType) {
        const customTypeHazards = getCustomTypeHazards(customType);
        allHazards.push(...customTypeHazards);

        // Add custom type label if different from component label
        if (customType.label && customType.label !== component.label) {
            customTypeItems.push({
                label: 'Typnamn',
                value: customType.label
            });
        }

        // Add material information from typeKey if available
        if (customType.material?.value) {
            customTypeItems.push({
                label: 'Material (typ)',
                value: translate(customType.material.value)
            });
        }

        // Add color information from custom type
        if (customType.color?.value) {
            customTypeItems.push({
                label: 'Färg (typ)',
                value: translate(customType.color.value.colorEnum)
            });
        }

        // Window-specific properties
        if ('paneType' in customType && (customType as WindowType).paneType?.value) {
            const paneType = (customType as WindowType).paneType.value;
            customTypeItems.push({
                label: 'Glastyp',
                value: paneType ? translate(paneType) : '-'
            });
        }

        // Door-specific properties
        if ('doorCategory' in customType && (customType as DoorType).doorCategory?.value) {
            const doorCategory = (customType as DoorType).doorCategory.value;
            customTypeItems.push({
                label: 'Dörrkategori',
                value: doorCategory ? translate(doorCategory) : '-'
            });
        }

        // Modular size for windows and doors
        if ('modularSize' in customType && (customType as WindowType | DoorType).modularSize?.value) {
            const modularSize = (customType as WindowType | DoorType).modularSize!.value!;
            customTypeItems.push({
                label: 'Modulmått',
                value: `${(modularSize.width * 1000).toFixed(0)} × ${(modularSize.height * 1000).toFixed(0)} mm`
            });
        }
    }

    if (allHazards.length > 0) {
        items.push({
            label: 'Miljö/hälsofara',
            value: allHazards
        });
    }

    // Collect all risks (component-level + custom type)
    const allRisks = new Array<string>();
    
    // Component-level risks
    if (component.buildingRisks?.value) {
        const riskConstruction = component.buildingRisks?.value;
        riskConstruction.risks?.map((risk)=> {
            return allRisks.push(translate(risk))
        })
        if (riskConstruction?.customRisk) {
            allRisks.push(riskConstruction.customRisk)
        }
    }

    // Custom type risks
    if (customType) {
        const customTypeRisks = getCustomTypeRisks(customType);
        allRisks.push(...customTypeRisks);
    }

    if (allRisks.length > 0) {
        items.push({
            label: 'Riskkonstruktion',
            value: allRisks
        });
    }

    if (component.areaShapes) {
        const area = component.totalArea(building);
        items.push({
                label: 'Area',
                value: area ? area.toFixed(1) + " kvm" : "-"
            })
        
        // Calculate area excluding windows if there are any
        if (windows.length > 0 && area) {
            const windowsArea = windows.reduce((total, window) => {
                const windowArea = window.totalArea(building);
                return total + (windowArea || 0);
            }, 0);
            
            const areaExcludingWindows = area - windowsArea;
            
            items.push({
                label: 'Area exkl fönster',
                value: areaExcludingWindows > 0 ? areaExcludingWindows.toFixed(1) + " kvm" : "-"
            });
        }
    }

    // Add child component counts to info panel
    sortedChildComponents.forEach(([baseType, components]) => {
        items.push({
            label: translate(baseType),
            value: `${components.length} st`
        });
    });

    if (component.depth) {
        const depth = component.depth.value
        items.push({
            label: 'Djup',
            value: depth ? (depth*1000) + " mm" : "-"
        })
    }

    if (component.insulation) {
        items.push({
            label: 'Isolering',
            value: component.insulation.value ? `${component.insulation.value}  mm` : "-"
        })
    }

    if (component.color) {
        items.push({
            label: 'Färg',
            value: `${translate(component.color?.value?.colorEnum ?? "-")}`
        })
    }

    items.push({
        label: 'Kommentar',
        value: component.comment.value ?? "-",
    })

    const defaultContainerStyle = {
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '30px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        height: 'fit-content',
        ...containerStyle
    };

    return (
        <div style={defaultContainerStyle}>
            {showHeader && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0 }}>{component.displayName(building)}</h3>
                    {headerActions}
                </div>
            )}
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                <div>
                    <h4 style={{ marginBottom: '15px' }}>Fakta</h4>
                    <InfoPanel items={items} />
                </div>

                {/* Custom Type Properties Section */}
                {customTypeItems.length > 0 && (
                    <div>
                        <h4 style={{ marginBottom: '15px' }}>Egenskaper från typ</h4>
                        <InfoPanel items={customTypeItems} />
                    </div>
                )}

                {/* Photo Location */}
                {component.photoLocation && (
                    <div>
                        <PhotoWithMarker 
                            photoLocation={component.photoLocation}
                            title="Position i foto"
                        />
                    </div>
                )}

                {/* Positions (Map and Blueprint Locations) */}
                {((component.mapLocations && Object.keys(component.mapLocations).length > 0) || 
                  (component.blueprintLocations && Object.keys(component.blueprintLocations).length > 0)) && (
                    <div>
                        <h4 style={{ marginBottom: '15px' }}>Position</h4>
                        <div>
                            {/* Map Locations */}
                            {component.mapLocations && Object.keys(component.mapLocations).length > 0 && building?.model?.mapImages && Object.keys(building.model.mapImages).length > 0 && 
                                Object.entries(component.mapLocations).map(([blueprintKey, location]) => {
                                    // Only show if this is the current map image key
                                    if (blueprintKey !== building.model.currentMapImageKey) return null;
                                    
                                    const mapImage = building.model.mapImages?.[blueprintKey];
                                    
                                    if (!mapImage) return null;
                                    
                                    // Create a component with the correct blueprintLocations structure
                                    const componentWithBlueprintLocation = new BaseComponentImpl({
                                        ...component,
                                        blueprintLocations: {
                                            [blueprintKey]: location
                                        }
                                    });
                                    
                                    return (
                                        <div key={`map-${blueprintKey}`} style={{maxWidth: '300px', marginBottom: '15px'}}>
                                            <BlueprintWithMarkers
                                                    blueprint={mapImage}
                                                    currentBlueprintKey={blueprintKey}
                                                    building={building}
                                                    utilities={[componentWithBlueprintLocation]}
                                                    title={""}
                                                />
                                        </div>
                                    );
                                })
                            }

                            {/* Blueprint Locations */}
                            {component.blueprintLocations && Object.keys(component.blueprintLocations).length > 0 && 
                                Object.entries(component.blueprintLocations).map(([blueprintKey, location]) => {
                                    // Find the blueprint image from the levels and check if it's the current blueprint
                                    let blueprintImage = null;
                                    let isCurrentBlueprint = false;
                                    
                                    // Search through all levels to find the blueprint
                                    if (building?.model?.structure?.levels) {
                                        for (const level of Object.values(building.model.structure.levels)) {
                                            if (level.blueprints?.[blueprintKey]) {
                                                blueprintImage = level.blueprints[blueprintKey];
                                                isCurrentBlueprint = level.currentBlueprintKey === blueprintKey;
                                                break;
                                            } else if (level.planImage && level.currentBlueprintKey === blueprintKey) {
                                                blueprintImage = level.planImage;
                                                isCurrentBlueprint = true;
                                                break;
                                            }
                                        }
                                    }
                                    
                                    // Only show if this is the current blueprint key
                                    if (!isCurrentBlueprint) return null;
                                    
                                    if (!blueprintImage) return null;
                                    
                                    return (
                                        <div key={`blueprint-${blueprintKey}`} style={{maxWidth: '300px', marginBottom: '15px'}}>
                                            <BlueprintWithMarkers
                                                    blueprint={blueprintImage}
                                                    currentBlueprintKey={blueprintKey}
                                                    building={building}
                                                    utilities={[component]}
                                                    title={""}
                                                />
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                )}

                {component.photos && (
                    <div>
                        <h4 style={{ marginBottom: '15px' }}>Foton</h4>
                        <PhotoGallery 
                            photos={component.photos} 
                            buildingKey={buildingKey}
                            onFancyboxOpen={onFancyboxOpen}
                            onFancyboxClose={onFancyboxClose}
                            onImageClick={onImageClick}
                        />
                    </div>
                )}

                {sortedChildComponents.map(([baseType, components]) => (
                    <div key={baseType}>
                        <h4 style={{ marginBottom: '15px' }}>{translate(baseType)} ({components.length} st)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                            {components.map((childComponent) => (
                                <ComponentCard
                                    key={childComponent.key}
                                    component={childComponent}
                                    building={building}
                                    buildingKey={buildingKey}
                                    onNavigate={(key) => navigate(`/building/${buildingKey}/component/${key}`)}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ComponentDetailsView;
